import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, TextField } from '@mui/material';
import { DocumentsSVG } from '@/components/Icon/Documents';
import * as S from '@/components/DocumentSharing/styles';
import { ExternalLimitList } from '@/components/DocLibrary/DocumentList/ShareToEmail';
import {
  ExternalInvitationDetails, ExternalSharedItem, InvitationErrorKey, getExternalSharedItem, invitationErrorKey,
  requestExternalShareAccessCode, verifyExternalShareAccessCode,
} from '@/api/documentShareInvitation.api';
import { openExternalShareDownload } from '@/utils/storageDownload';
import { convertToFileSize } from '@/utils/convert';

interface Props { token: string; details: ExternalInvitationDetails; dateFormat: Intl.DateTimeFormat }

/**
 * Account-free access for an external recipient. The session token lives in memory only, so closing the tab
 * or reaching the session limit always requires a new code from the invited mailbox.
 */
export default function ExternalInvitation({ token, details, dateFormat }: Props) {
  // @ts-ignore i18next's deeply nested translation key type exceeds CRA's incremental compiler limit.
  const { t } = useTranslation();
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [session, setSession] = useState<{ token: string; expiresAt: Date } | null>(null);
  const [item, setItem] = useState<ExternalSharedItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<InvitationErrorKey | ''>('');
  const locked = error === 'locked' || (!!details.locked_until && new Date(details.locked_until) > new Date());

  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true); setError('');
    try { await action(); } catch (e) {
      const key = invitationErrorKey(e);
      setError(key);
      // An ended session falls back to the code step instead of leaving a dead download button.
      if (key === 'sessionExpired') { setSession(null); setItem(null); setCodeSent(false); setCode(''); }
    } finally { setBusy(false); }
  };
  const sendCode = () => run(async () => {
    await requestExternalShareAccessCode(token);
    setCodeSent(true); setCode('');
  });
  const verify = () => run(async () => {
    const { data } = await verifyExternalShareAccessCode(token, code.trim());
    const verified = { token: data.data.access_session_token, expiresAt: new Date(Date.now() + data.data.expires_in * 1000) };
    const loaded = await getExternalSharedItem(verified.token);
    setSession(verified); setItem(loaded.data.data); setCode('');
  });
  const download = () => run(async () => {
    if (!session) return;
    await openExternalShareDownload(session.token);
    const refreshed = await getExternalSharedItem(session.token);
    setItem(refreshed.data.data);
  });

  if (session && item) {
    const exhausted = item.downloads_remaining === 0;
    return <>
      <S.Description>{t('documentEmailShare.acceptedDescription')}</S.Description>
      <S.Target style={{ margin: '28px 0 0' }}>
        <S.FileIcon aria-hidden="true"><DocumentsSVG /></S.FileIcon>
        <div style={{ minWidth: 0 }}><small>{t('documentEmailShare.target.document')}</small><strong>{item.name || t('documentEmailShare.sharedItem')}</strong></div>
      </S.Target>
      <S.DetailList>
        {item.size !== null && <div><dt>{t('documentEmailShare.fileSize')}</dt><dd>{convertToFileSize(Number(item.size))}</dd></div>}
        <div><dt>{t('documentEmailShare.permission')}</dt><dd>{t('documentEmailShare.readDownload')}</dd></div>
        {item.downloads_remaining !== null && <div><dt>{t('documentEmailShare.download')}</dt><dd>{t('documentEmailShare.downloadsRemaining', { count: item.downloads_remaining })}</dd></div>}
        <div><dt>{t('documentEmailShare.sessionEnds')}</dt><dd><time dateTime={session.expiresAt.toISOString()}>{dateFormat.format(session.expiresAt)}</time></dd></div>
        <div><dt>{t('documentEmailShare.expires')}</dt><dd><time dateTime={item.expires_at}>{dateFormat.format(new Date(item.expires_at))}</time></dd></div>
      </S.DetailList>
      {error && <S.Feedback $error role="alert">{t(`documentEmailShare.errors.${error}`)}</S.Feedback>}
      <S.PageActions>
        <S.PrimaryButton type="button" disabled={busy || exhausted} aria-busy={busy} onClick={() => void download()}>
          {busy && <CircularProgress size={18} aria-hidden="true" />}{t('documentEmailShare.download')}
        </S.PrimaryButton>
      </S.PageActions>
      <ExternalLimitList limits={item.limits} />
    </>;
  }

  return <>
    <S.Description>{t('documentEmailShare.externalDescription', { email: details.masked_email })}</S.Description>
    <S.DetailList>
      <div><dt>{t('documentEmailShare.permission')}</dt><dd>{t('documentEmailShare.readDownload')}</dd></div>
      <div><dt>{t('documentEmailShare.expires')}</dt><dd><time dateTime={details.expires_at}>{dateFormat.format(new Date(details.expires_at))}</time></dd></div>
    </S.DetailList>
    <ExternalLimitList limits={details.limits} />
    {error && <S.Feedback $error role="alert">{t(`documentEmailShare.errors.${error}`)}</S.Feedback>}
    {locked && details.locked_until && error !== 'locked' && <S.Feedback $error role="alert">
      {t('documentEmailShare.lockedUntil', { time: dateFormat.format(new Date(details.locked_until)) })}
    </S.Feedback>}
    {codeSent && !error && <S.Feedback role="status" aria-live="polite">{t('documentEmailShare.codeSent', { minutes: details.limits.code_minutes })}</S.Feedback>}
    {codeSent && <S.CodeRow>
      <TextField id="external-share-code" label={t('documentEmailShare.codeLabel')} value={code} disabled={busy || locked}
        onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
        inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6, pattern: '\\d{6}' }} />
      <S.PrimaryButton type="button" disabled={busy || locked || code.length !== 6} aria-busy={busy} onClick={() => void verify()}>
        {busy && <CircularProgress size={18} aria-hidden="true" />}{t('documentEmailShare.verify')}
      </S.PrimaryButton>
    </S.CodeRow>}
    <S.PageActions>
      <S.SecondaryButton type="button" disabled={busy || locked} onClick={() => void sendCode()}>
        {t(codeSent ? 'documentEmailShare.resendCode' : 'documentEmailShare.sendCode')}
      </S.SecondaryButton>
    </S.PageActions>
  </>;
}
